import { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { discoArrayToProductImageStruct, getProductImageAltText } from "helpers/product-image-helpers";
import { PdpImageZoomModalName, analytics } from "rtr-constants";
import Header from "./header";
import PDPBreadCrumbs from "../products/pdp-breadcrumbs";
import PDPImagery from "../pdp/pdp-imagery";
import PdpImageZoomModal from "../products/pdp-image-zoom-modal.jsx";
import ReviewModal from "./review-modal";
import ProductDetails from "../products/product-details";
import ProductImageRotation from "helpers/product-image-rotation";
import ProductInfo from "../products/product-info/product-info";
import ReviewThumbNailCarousel from "./review-thumbnail/review-thumbnail-carousel";
import AnchorProductAggregateRating from "components/source/products/anchor-product-aggregate-rating";
import sharedActions from "actions/shared-actions";
import useSailthruConfig from "./useSailthruConfig";
import PDPInlineReviews from "../reviews/pdp-inline-reviews";
import { MODAL_NAMES } from "constants/modalNames";
import PlansDrawerModal from "../acquisition/plans_drawer/plans-drawer-modal";
import MembershipBuyBox from "./membership-buy-box";
import ClearanceBuyBox from "./clearance-buy-box";
import maternityHelper from "helpers/maternity-helper";
import PdpCarousels from "../pdp/pdp-carousels";
import { Flags } from "../hoc/with-feature-flags";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { isAdmin } from "components/source/hoc/with-user-data";
import PDPAdminDetail from "components/source/products/pdp-admin-detail.tsx";
import ComingSoonPDPContent from "components/source/products/coming-soon-pdp-content";

export default function PDP() {
  useSailthruConfig();
  const [featureImageSelected, setFeatureImageSelected] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalIndex, setReviewModalIndex] = useState(0);
  const inlineReviewsRef = useRef(null);
  const isBuyNow = false;

  const userData = useSelector(state => state.userData);

  const staticProduct = useSelector(state => state.staticProduct);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const workingFilters = useSelector(state => state.workingFilters);
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const gridProductDrawer = useSelector(state => state.gridProductDrawer);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);

  const reviewStarRating = useSelector(state => state.reviews?.averageRating ?? 0);
  const numOfReviews = useSelector(state => state.reviews?.count ?? 0);
  const unlimitedAddOnFlag = useSelector(state => state.featureFlags?.[Flags.UNLIMITED_ALWAYS_ON_ADD_ONS]);

  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const dispatch = useDispatch();

  const showMaternityBumpImage = maternityHelper.showMaternityBumpImage(userData);

  // FIXME: setting maternity flag to false for now
  const imageRotation = ProductImageRotation(
    workingFilters?.canonicalSizes || [],
    staticProduct,
    showMaternityBumpImage
  );
  const altText = getProductImageAltText(staticProduct.displayName, staticProduct.designerDisplayName);
  const images = discoArrayToProductImageStruct(staticProduct.images);
  const displayedModal = useSelector(state => state.displayedModal);

  const isAccessory = Array.isArray(staticProduct?.skus) && staticProduct.skus.length === 1;

  const annotationSuffix = MembershipUpgradesHelpers.getUpgradeAnnotationAppendCE(
    membershipState,
    userData,
    ceBagCount,
    unlimitedAddOnFlag
  );

  return (
    <div className="pdp-content">
      <div data-test-id="universal_pdp" className="pdp-main-section media-query-layout">
        <PDPImagery
          altText={altText}
          className={"product-images"}
          isCloudZoomEnabled={false}
          images={images}
          imageRotation={imageRotation}
          isMobileViewport={isMobileViewport}
          onClick={(index, ref) => {
            setFeatureImageSelected({ index, ref });
            dispatch(sharedActions.displayModal(PdpImageZoomModalName));
          }}
        />

        <div className="pdp-dynamic-section">
          <Header />
          <ComingSoonPDPContent
            isSaveToHearts={staticProduct?.isComingSoon}
            location={analytics.ACTION_LOCATIONS.PDP}
            product={staticProduct}
          />
          <div className="mt-8 md:mt-6" data-test-id="review-stars-count">
            <AnchorProductAggregateRating
              productId={staticProduct.productId}
              reviews={{
                count: numOfReviews,
                averageRating: reviewStarRating,
              }}
              redirectUrl={gridProductDrawer?.pdpUrl}
            />
            <ReviewThumbNailCarousel
              setReviewModalOpen={setReviewModalOpen}
              setReviewModalIndex={setReviewModalIndex}
              productId={staticProduct.productId}
            />
          </div>
          {!staticProduct?.isComingSoon && (
            <>
              <hr className="border-offWhite mt-8"></hr>

              {staticProduct.merchandisingCategory === "Rental" && <MembershipBuyBox />}
              {staticProduct.merchandisingCategory === "Clearance" && <ClearanceBuyBox />}
            </>
          )}
        </div>

        <PdpImageZoomModal
          altText={altText}
          imageRotation={imageRotation}
          images={images}
          isMobileViewport={isMobileViewport}
          openingElement={featureImageSelected}
        />
      </div>

      <ProductInfo
        selected={null}
        showOptionalDetails={isBuyNow}
        isMobileViewport={isMobileViewport}
        imageRotation={imageRotation}
        images={images}
        displayName={staticProduct.displayName}
        designerDisplayName={staticProduct.designerDisplayName}
        productId={staticProduct.productId}
        fitNotes={staticProduct.fitNotes}
        stylistNotes={staticProduct.stylistNotes}
        productDetails={staticProduct.productDetails}
      />

      {!isMobileViewport && (
        <ProductDetails
          selected={null}
          showOptionalDetails={isBuyNow}
          isMobileViewport={isMobileViewport}
          imageRotation={imageRotation}
          images={images}
          displayName={staticProduct.displayName}
          designerDisplayName={staticProduct.designerDisplayName}
          productId={staticProduct.productId}
          fitNotes={staticProduct.fitNotes}
          stylistNotes={staticProduct.stylistNotes}
          productDetails={staticProduct.productDetails}
        />
      )}

      <PdpCarousels
        annotationSuffix={annotationSuffix}
        dispatch={dispatch}
        isRentable={staticProduct.merchandisingCategory === "Rental"}
        isAccessory={isAccessory}
        isUnavailableToRent={false}
        isUPDP={true}
      />
      {!isMobileViewport && <div data-test-id="desktop-reviews-partial-anchor" id="reviews-partial" />}
      {!isMobileViewport && (
        <PDPInlineReviews
          dispatch={dispatch}
          key="0"
          ref={inlineReviewsRef}
          product={staticProduct}
          shouldLoadReviews={true}
          writingReview={false} // @TODO: Ensure writingReview works as in PDP - did some digging but couldn't find where
          // this value is set in legacy PDP (seems to be present in ruby)
        />
      )}

      <PDPBreadCrumbs />
      {reviewsThumbnailPhotos?.length > 0 && reviewModalOpen && (
        <ReviewModal
          productDisplayName={staticProduct?.displayName}
          productId={staticProduct?.productId}
          ind={reviewModalIndex}
          setIsOpen={setReviewModalOpen}
          isOpen={reviewModalOpen}
        />
      )}
      {isAdmin(userData) && (
        <PDPAdminDetail id={staticProduct?.productId} published={staticProduct?.stage === "PUBLISHED"} />
      )}
      <PlansDrawerModal isOpen={displayedModal === MODAL_NAMES.PLANS_DRAWER} />
    </div>
  );
}
